// ─── Pip Calculator ────────────────────────────────────────────────
// Pure functions for forex pip value calculation and position sizing.
// Supports: forex pairs, gold (XAU), silver (XAG), oil (CL/WTI/NG/BRENT)
// All monetary values are in USD.

import { ASSET_ID_MAP } from './ctrader-parsers';

// ─── Types ─────────────────────────────────────────────────────────

export interface SymbolMeta {
  symbolName: string;
  baseCurrency: string;
  quoteCurrency: string;
  digits: number;
  /** cTrader ProtoOASymbol.pipPosition — the digit index of the pip (0 = units) */
  pipPosition: number | null;
  /** Contract size per 1 standard lot (from cTrader lotSize/baseUnit or default) */
  lotSize: number;
}

export interface PipValueResult {
  /** Pip size (e.g., 0.0001 for EURUSD, 0.01 for USDJPY) */
  pipSize: number;
  /** Contract size per 1 standard lot */
  contractSize: number;
  /** Pip value for 1 standard lot, in USD */
  pipValuePerLot: number;
  /** Pip value for 1 unit of volume, in USD */
  pipValuePerUnit: number;
  /** The conversion rate used (quote → USD) */
  conversionRate: number;
  /** Base currency of the pair */
  baseCurrency: string;
  /** Quote currency of the pair */
  quoteCurrency: string;
}

export interface LotSizeResult {
  /** Recommended lot size (in standard lots) */
  lots: number;
  /** Volume units (for cTrader: lots * contractSize) */
  volumeUnits: number;
  /** Exact monetary risk per pip for this lot size */
  riskPerPip: number;
  /** Total risk at stop loss distance */
  totalRisk: number;
}

// ─── Symbol Name Parsing ───────────────────────────────────────────

/** Extract base currency from symbol name */
export function extractBaseCurrency(symbolName: string): string {
  const upper = symbolName.toUpperCase();
  // Commodities / indices with prefix
  if (upper.startsWith('XAU')) return 'XAU';
  if (upper.startsWith('XAG')) return 'XAG';
  if (upper.startsWith('XTI') || upper.startsWith('USOIL') || upper.startsWith('WTI') || upper.startsWith('CL')) return 'OIL';
  if (upper.startsWith('XBR') || upper.startsWith('UKOIL') || upper.startsWith('BRENT')) return 'BRENT';
  if (upper.startsWith('NG')) return 'NG';
  // Standard forex: first 3 chars = base, last 3 = quote
  if (upper.length >= 6) return upper.slice(0, 3);
  return upper;
}

/** Extract quote currency from symbol name */
export function extractQuoteCurrency(symbolName: string): string {
  const upper = symbolName.toUpperCase();
  // Commodities: quote is always the suffix after the commodity code
  if (upper.startsWith('XAU')) return upper.length >= 6 ? upper.slice(-3) : 'USD';
  if (upper.startsWith('XAG')) return upper.length >= 6 ? upper.slice(-3) : 'USD';
  // Oil typically quoted in USD
  if (upper.startsWith('XTI') || upper.startsWith('USOIL') || upper.startsWith('WTI') || upper.startsWith('CL')) return 'USD';
  if (upper.startsWith('XBR') || upper.startsWith('UKOIL') || upper.startsWith('BRENT')) return 'USD';
  if (upper.startsWith('NG')) return 'USD';
  // Standard forex: last 3 chars = quote
  if (upper.length >= 6) return upper.slice(-3);
  return upper;
}

// ─── Pip Size — defined by asset class, NOT by digits ──────────────
// 1 pip = 0.0001 for most forex
// 1 pip = 0.01   for JPY pairs, gold, silver, oil
//
// cTrader's pipPosition is the authoritative source:
//   digits=5, pipPosition=4 → pip = 10^(-4) = 0.0001
//   digits=3, pipPosition=2 → pip = 10^(-2) = 0.01

/**
 * Classify a symbol name into an asset class.
 * Uses `includes` (NOT `startsWith`) and also recognises broker aliases
 * (GOLD, SILVER, …) so a prefixed / suffixed symbol name can never fall
 * through to the wrong asset class → wrong pip size.
 */
export function classifySymbol(symbolName: string): 'FX' | 'JPY' | 'XAU' | 'XAG' | 'OIL' | 'NG' {
  const upper = String(symbolName ?? '').toUpperCase();
  if (upper.includes('JPY')) return 'JPY';
  if (upper.includes('XAU') || upper.includes('GOLD')) return 'XAU';
  if (upper.includes('XAG') || upper.includes('SILVER')) return 'XAG';
  if (
    upper.includes('XTI') || upper.includes('WTI') ||
    upper.includes('OIL') || upper.includes('BRENT') || upper.includes('XBR')
  ) {
    return 'OIL';
  }
  if (upper.includes('NATGAS') || upper.includes('NGAS')) return 'NG';
  return 'FX';
}

export function getPipSize(symbolName: string, pipPosition?: number | null): number {
  // If cTrader provides pipPosition, use it as authoritative
  if (pipPosition !== null && pipPosition !== undefined && pipPosition >= 0) {
    return Math.pow(10, -pipPosition);
  }

  // Fallback: heuristic by asset class (broker convention)
  //   FX                                  → 0.0001
  //   JPY / XAU / XAG / XTI (+ oil / NG)  → 0.01
  const cls = classifySymbol(symbolName);
  if (cls === 'JPY' || cls === 'XAU' || cls === 'XAG' || cls === 'OIL' || cls === 'NG') {
    return 0.01;
  }
  return 0.0001;
}

// ─── Contract Size ─────────────────────────────────────────────────
// Standard lot contract sizes per asset class.
// cTrader lotSize/baseUnit field is authoritative when available.

export const DEFAULT_CONTRACT_SIZES: Record<string, number> = {
  XAU: 100,    // 100 troy oz per standard lot
  XAG: 5000,   // 5000 troy oz per standard lot
  OIL: 1000,   // 1000 barrels per standard lot (WTI/CL)
  BRENT: 1000, // 1000 barrels per standard lot
  NG: 10000,   // 10000 MMBtu per standard lot
};

export function getContractSize(symbolName: string, ctraderLotSize?: number | null): number {
  // cTrader lotSize is authoritative when available and > 0
  if (ctraderLotSize !== null && ctraderLotSize !== undefined && ctraderLotSize > 0) {
    // Sanity check: if lotSize looks like a step/min volume (e.g. 1000 for forex),
    // it's probably NOT the actual contract size. Standard forex = 100000.
    const base = extractBaseCurrency(symbolName);
    if (DEFAULT_CONTRACT_SIZES[base]) {
      // Commodity — trust cTrader's lotSize
      return ctraderLotSize;
    }
    // Forex: cTrader sometimes reports stepVolume as lotSize
    // Heuristic: if lotSize < 10000, it's likely step volume, not contract size
    if (ctraderLotSize < 10000) return 100000;
    return ctraderLotSize;
  }

  // Default contract sizes by asset class
  const base = extractBaseCurrency(symbolName);
  if (DEFAULT_CONTRACT_SIZES[base]) return DEFAULT_CONTRACT_SIZES[base];

  // Standard forex: 100,000 units per lot
  return 100000;
}

// ─── Pip Value Calculator ──────────────────────────────────────────

/**
 * Calculate pip value for 1 standard lot, in USD.
 *
 * Formula:
 *   pipValue = contractSize * pipSize * conversionRate
 *
 * Where conversionRate converts quote currency → USD:
 *   - Quote = USD (EURUSD):   conversionRate = 1
 *   - Base = USD (USDJPY):    conversionRate = 1 / USDXXX_price
 *   - Cross pair (EURGBP):    conversionRate = GBPUSD_price
 *   - Gold/Silver/Oil in USD: conversionRate = 1
 *
 * @param symbolName  - e.g. "EURUSD", "XAUUSD", "GBPJPY"
 * @param pipSize     - from getPipSize()
 * @param contractSize - from getContractSize() or cTrader lotSize
 * @param quoteToUsdRate - price to convert 1 unit of quote currency → USD.
 *     Set to 1 if quote IS USD.
 *     For USDJPY: pass the current price of USDJPY (this converts JPY→USD: 1/USDJPY)
 *     For EURGBP: pass the current price of GBPUSD
 *     For EURJPY: pass the current price of USDJPY
 */
export function calcPipValuePerLot(
  symbolName: string,
  pipSize: number,
  contractSize: number,
  quoteToUsdRate: number,
): number {
  return contractSize * pipSize * quoteToUsdRate;
}

/**
 * Full pip value calculation with all metadata.
 * This is the main function you'd call with symbol info + exchange rates.
 */
export function calculatePipValue(
  symbolName: string,
  options: {
    pipPosition?: number | null;
    ctraderLotSize?: number | null;
    /** Rate to convert quote currency → USD. Default 1 (quote IS USD). */
    quoteToUsdRate?: number;
  } = {},
): PipValueResult {
  const pipSize = getPipSize(symbolName, options.pipPosition);
  const contractSize = getContractSize(symbolName, options.ctraderLotSize);
  const conversionRate = options.quoteToUsdRate ?? 1;
  const pipValuePerLot = calcPipValuePerLot(symbolName, pipSize, contractSize, conversionRate);
  const pipValuePerUnit = pipValuePerLot / contractSize;

  return {
    pipSize,
    contractSize,
    pipValuePerLot,
    pipValuePerUnit,
    conversionRate,
    baseCurrency: extractBaseCurrency(symbolName),
    quoteCurrency: extractQuoteCurrency(symbolName),
  };
}

// ─── Position Sizing ───────────────────────────────────────────────

/**
 * Calculate lot size to risk exactly X USD over Y pips stop loss.
 *
 * Formula:
 *   lots = riskAmount / (stopLossPips * pipValuePerLot)
 *
 * @param riskAmount    - max USD you're willing to lose (e.g., 100)
 * @param stopLossPips  - stop loss distance in pips (e.g., 20)
 * @param pipValuePerLot - pip value for 1 standard lot (from calculatePipValue)
 * @param minLots       - minimum lot size (default 0.01)
 * @param maxLots       - maximum lot size (default 100)
 * @param lotStep       - lot increment step (default 0.01 for forex)
 */
export function calculateLotSize(
  riskAmount: number,
  stopLossPips: number,
  pipValuePerLot: number,
  options: {
    minLots?: number;
    maxLots?: number;
    lotStep?: number;
  } = {},
): LotSizeResult {
  const { minLots = 0.01, maxLots = 100, lotStep = 0.01 } = options;

  if (riskAmount <= 0 || stopLossPips <= 0 || pipValuePerLot <= 0) {
    return { lots: 0, volumeUnits: 0, riskPerPip: 0, totalRisk: 0 };
  }

  const riskPerPip = riskAmount / stopLossPips;
  let rawLots = riskPerPip / pipValuePerLot;

  // Round down to nearest lot step
  let lots = Math.floor(rawLots / lotStep) * lotStep;

  // Clamp
  lots = Math.max(minLots, Math.min(maxLots, lots));

  // Round to avoid floating point issues
  const decimals = lotStep < 0.1 ? 2 : 0;
  lots = parseFloat(lots.toFixed(decimals));

  return {
    lots,
    volumeUnits: Math.round(lots * getContractSize('', null)), // will be overridden by caller
    riskPerPip: lots * pipValuePerLot,
    totalRisk: stopLossPips * lots * pipValuePerLot,
  };
}

/**
 * Convenience: one-call position sizing with all parameters.
 * You provide: symbol name, cTrader symbol detail, risk params → get lot size.
 */
export function sizePosition(
  symbolName: string,
  riskAmount: number,
  stopLossPips: number,
  options: {
    pipPosition?: number | null;
    ctraderLotSize?: number | null;
    quoteToUsdRate?: number;
    minLots?: number;
    maxLots?: number;
    lotStep?: number;
  } = {},
): { pipValue: PipValueResult; lotSize: LotSizeResult } {
  const pipValue = calculatePipValue(symbolName, {
    pipPosition: options.pipPosition,
    ctraderLotSize: options.ctraderLotSize,
    quoteToUsdRate: options.quoteToUsdRate,
  });

  const contractSize = pipValue.contractSize;
  const lotSizeResult = calculateLotSize(riskAmount, stopLossPips, pipValue.pipValuePerLot, {
    minLots: options.minLots,
    maxLots: options.maxLots,
    lotStep: options.lotStep,
  });

  // Fill in correct volumeUnits
  lotSizeResult.volumeUnits = Math.round(lotSizeResult.lots * contractSize);

  return { pipValue, lotSize: lotSizeResult };
}

// ─── Conversion Rate Helpers ───────────────────────────────────────

/**
 * Determine the pair needed to convert quote currency → USD.
 * Returns the symbol name to look up (e.g., "GBPUSD", "USDJPY").
 * Returns null if quote IS USD (no conversion needed).
 */
export function getConversionPair(symbolName: string): string | null {
  const quote = extractQuoteCurrency(symbolName);
  if (quote === 'USD') return null;

  const base = extractBaseCurrency(symbolName);
  // If base is USD (e.g., USDJPY, USDCAD), conversion is 1/price
  // The user needs to pass the current price of this pair
  if (base === 'USD') return symbolName;

  // Cross pair: need quote→USD rate
  // e.g., EURGBP → need GBPUSD
  return quote + 'USD';
}

/**
 * Describe what the quoteToUsdRate parameter means for a given symbol,
 * so callers understand what price to pass.
 */
export function describeConversionRate(symbolName: string): {
  mode: 'direct' | 'inverse' | 'cross';
  conversionPair: string | null;
  description: string;
} {
  const quote = extractQuoteCurrency(symbolName);
  const base = extractBaseCurrency(symbolName);

  if (quote === 'USD') {
    return {
      mode: 'direct',
      conversionPair: null,
      description: `Quote is USD — pip value is direct: ${symbolName} pip = contractSize × pipSize. No conversion needed.`,
    };
  }

  if (base === 'USD') {
    return {
      mode: 'inverse',
      conversionPair: symbolName,
      description: `Base is USD, quote is ${quote} — pass 1/${symbolName}_price as quoteToUsdRate. Example: USDJPY @ 150 → quoteToUsdRate = 1/150 ≈ 0.00667`,
    };
  }

  const conversionPair = quote + 'USD';
  return {
    mode: 'cross',
    conversionPair,
    description: `Cross pair — need ${conversionPair} price as quoteToUsdRate. Example: EURGBP → pass GBPUSD price (e.g., 1.25) as quoteToUsdRate.`,
  };
}

// ─── Quick Reference Table ─────────────────────────────────────────

/**
 * Returns pip value for 1 standard lot in USD, using standard contract sizes
 * and assuming quote IS USD (no cross-rate conversion).
 * Quick reference only — use calculatePipValue() for real trading.
 */
export function quickPipValueUSD(symbolName: string): number {
  const pipSize = getPipSize(symbolName);
  const contractSize = getContractSize(symbolName);
  return contractSize * pipSize;
}

// ─── Examples (not exported, reference only) ──────────────────────
// EURUSD @ 1.1456:
//   pipSize = 0.0001, contractSize = 100000, quote=USD → conversionRate = 1
//   pipValuePerLot = 100000 * 0.0001 * 1 = $10.00
//   Risk $500, SL 20 pips → lots = 500 / (20 * 10) = 2.5 lots
//
// XAUUSD @ 2300:
//   pipSize = 0.01, contractSize = 100, quote=USD → conversionRate = 1
//   pipValuePerLot = 100 * 0.01 * 1 = $1.00
//   Risk $100, SL 50 pips → lots = 100 / (50 * 1) = 2.0 lots
//
// USDJPY @ 150:
//   pipSize = 0.01, contractSize = 100000, quote=JPY → conversionRate = 1/150
//   pipValuePerLot = 100000 * 0.01 * (1/150) = 1000/150 = $6.67
//   Risk $200, SL 30 pips → lots = 200 / (30 * 6.67) ≈ 1.0 lot
//
// EURGBP @ 0.85 (GBPUSD @ 1.25):
//   pipSize = 0.0001, contractSize = 100000, quote=GBP → conversionRate = 1.25
//   pipValuePerLot = 100000 * 0.0001 * 1.25 = $12.50
//   Risk $250, SL 20 pips → lots = 250 / (20 * 12.5) = 1.0 lot